import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Check, Loader2, Copy, Share2, Users, Gift } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_8cb5d32c-b1ce-4951-b77e-897f98e6cc9f/artifacts/uyctypm7_41ed1e03-8bc9-42a0-8314-b43b52fec9a9.JPG";

export default function EmailVerifiedPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (token) {
      verifyEmail();
    } else {
      setError("No verification token provided");
      setLoading(false);
    }
  }, [token]);

  const verifyEmail = async () => {
    try {
      const response = await fetch(`${API_URL}/api/verify-email?token=${token}`, {
        credentials: "include"
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || "Verification failed");
      }
      setResult(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const referralLink = result?.referral_code
    ? `https://www.afrofanthem.com/survey?ref=${result.referral_code}`
    : null;

  const copyReferralLink = async () => {
    if (referralLink) {
      try {
        await navigator.clipboard.writeText(referralLink);
        toast.success("Referral link copied!");
      } catch (e) {
        toast.error("Failed to copy link");
      }
    }
  };

  const shareWhatsApp = () => {
    const text = referralLink
      ? `I just made my voice heard for African football fans! Join the movement and we'll both earn extra prize draw entries: ${referralLink}`
      : "I just made my voice heard for African football fans! Join the movement at https://www.afrofanthem.com";
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  };

  const shareGeneral = async () => {
    const text = referralLink
      ? `I just made my voice heard for African football fans! Join me: ${referralLink}`
      : "I just made my voice heard for African football fans! Join the movement at https://www.afrofanthem.com";
    
    try {
      if (navigator.share) {
        await navigator.share({ title: "Afro Fanthem", text, url: referralLink || "https://www.afrofanthem.com" });
      } else {
        await navigator.clipboard.writeText(text);
        toast.success("Link copied to clipboard!");
      }
    } catch {
      toast.error("Sharing not supported on this device");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#FBBF24] animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Verifying your email...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0A0A0A]">
        <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-black/60 border-b border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center h-16">
              <Link to="/" className="flex items-center gap-3">
                <img src={LOGO_URL} alt="Afro Fanthem" className="h-10 w-auto" />
              </Link>
            </div>
          </div>
        </header>
        <main className="pt-24 pb-16 px-4">
          <div className="max-w-xl mx-auto text-center">
            <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="text-red-500 text-3xl font-bold">!</span>
            </div>
            <h1 className="font-heading text-4xl text-white uppercase tracking-tight mb-4" data-testid="verify-error-headline">
              Verification Failed
            </h1>
            <p className="text-gray-400 mb-8">{error}</p>
            <Link to="/" className="inline-flex items-center gap-2 bg-[#FBBF24] text-black font-bold uppercase tracking-widest px-8 py-4 hover:bg-[#F59E0B] transition-all">
              Return Home
            </Link>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-black/60 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <Link to="/" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Afro Fanthem" className="h-10 w-auto" />
            </Link>
          </div>
        </div>
      </header>

      <main className="pt-24 pb-16 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Success */}
          <div className="text-center mb-12">
            <div className="w-20 h-20 bg-[#10B981]/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-[#10B981]" />
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl text-white uppercase tracking-tight mb-4" data-testid="verified-headline">
              {result?.status === "already_verified" ? "Already Verified!" : "Email Verified!"}
            </h1>
            <p className="text-xl text-gray-300 mb-2">
              Your voice has been validated and added to the movement.
            </p>
            <p className="text-gray-400">
              Together, we're building a credible case for fan representation and inclusion.
            </p>
          </div>

          {/* Share CTA - Primary */}
          <div className="bg-[#25D366]/10 border border-[#25D366]/30 p-6 sm:p-8 rounded-sm mb-8 text-center">
            <h2 className="font-heading text-2xl text-white uppercase mb-3">
              Spread the Word
            </h2>
            <p className="text-gray-400 mb-6">
              Every voice strengthens the movement. Share with your friends and community!
            </p>
            <button
              onClick={shareWhatsApp}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-[#25D366] text-white font-bold uppercase tracking-widest px-8 py-4 hover:bg-[#20BA5A] transition-all mb-3"
              data-testid="whatsapp-share-button"
            >
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              Share on WhatsApp
            </button>
            <button
              onClick={shareGeneral}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-white/10 border border-white/20 text-white font-bold uppercase tracking-widest px-8 py-4 hover:bg-white/20 transition-colors"
              data-testid="general-share-button"
            >
              <Share2 className="w-5 h-5" />
              Share via Other
            </button>
          </div>

          {/* Referral Section */}
          {referralLink && (
            <div className="bg-[#10B981]/10 border border-[#10B981]/30 p-6 rounded-sm mb-8">
              <div className="flex items-center gap-3 mb-4">
                <Users className="w-6 h-6 text-[#10B981]" />
                <h3 className="font-heading text-xl text-white uppercase">Earn Extra Prize Entries</h3>
              </div>
              <p className="text-gray-300 text-sm mb-4">
                Share your unique link. When friends complete the survey with a phone number, 
                you both get an extra prize draw entry!
              </p>
              <div className="flex items-center gap-2 bg-black/30 p-3 border border-white/10">
                <input 
                  type="text" 
                  readOnly 
                  value={referralLink}
                  className="flex-1 bg-transparent text-white text-sm outline-none truncate"
                />
                <button
                  onClick={copyReferralLink}
                  className="flex items-center gap-2 text-[#10B981] hover:text-[#10B981]/80 transition-colors px-3 py-1"
                  data-testid="copy-referral-button"
                >
                  <Copy className="w-4 h-4" />
                  Copy
                </button>
              </div>
            </div>
          )}

          {/* Back to Home */}
          <div className="text-center space-y-3">
            <Link 
              to="/forum"
              className="inline-flex items-center justify-center gap-2 bg-[#FBBF24] text-black font-bold uppercase tracking-widest px-8 py-4 hover:bg-[#F59E0B] transition-all"
              data-testid="join-forum-link"
            >
              Join the Forum Debate
            </Link>
            <div>
              <Link 
                to="/"
                className="text-gray-400 hover:text-white transition-colors underline"
                data-testid="back-home-link"
              >
                Return to Homepage
              </Link>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
